/**
 * deviceAuth — Pure HMAC-SHA-256 signing helpers for device auth headers.
 *
 * Ported byte-for-byte from
 * `apps/extension/src/services/device/deviceAuth.ts:11-26` (the wire
 * algorithm is locked by the server-side `device-signature-guard.ts`
 * verifier, see `docs/architecture/phase-5-device-header-spec.md` §5).
 *
 * The basis is `METHOD\nPATH\nTIMESTAMP\nBODY\nSECRET` (LF-joined,
 * NOT JSON). Output is lowercase hex SHA-256.
 *
 * Refs:
 * - 4.功能规划.md §2.2 — `deviceAuth.ts HMAC 签名(纯 node:crypto,直接搬)`
 * - `docs/architecture/phase-5-device-header-spec.md` §5 (signature format)
 */

import { createHash } from "node:crypto";

/** Canonical header names — MUST match server's `device-signature-guard.ts:9-15`. */
export const DeviceAuthHeaders = {
	deviceId: "x-ms-device-id",
	deviceSecret: "x-ms-device-secret",
	signature: "x-ms-device-signature",
	timestamp: "x-ms-device-timestamp",
	secretVersion: "x-ms-device-secret-version",
} as const;

export interface DeviceRequestSignatureParams {
	method: string;
	path: string;
	timestamp: number;
	body: string;
	secret: string;
}

/**
 * Compute the HMAC-SHA-256 hex digest of the canonical basis.
 *
 * Server contract (`apps/server/src/lib/auth/device-signature-guard.ts:46-62`)
 * is line-for-line identical: same LF-joined basis, same lowercase
 * hex output. Any divergence breaks `device-signature-guard.test.ts`.
 */
export function createDeviceRequestSignature(params: DeviceRequestSignatureParams): string {
	const basis = [
		params.method.toUpperCase(),
		params.path,
		String(params.timestamp),
		params.body,
		params.secret,
	].join("\n");
	return createHash("sha256").update(basis).digest("hex");
}

/** 5-header map consumed by `fetch()` callers (CLI bridge, Extension). */
export interface DeviceSignedHeaders {
	[DeviceAuthHeaders.deviceId]: string;
	[DeviceAuthHeaders.deviceSecret]: string;
	[DeviceAuthHeaders.signature]: string;
	[DeviceAuthHeaders.timestamp]: string;
	[DeviceAuthHeaders.secretVersion]: string;
}

export interface BuildSignedHeadersParams {
	method: string;
	path: string;
	body: string;
	publicId: string;
	deviceSecret: string;
	secretVersion: number;
	/** Override for deterministic tests. */
	timestamp?: number;
}

/**
 * Build the canonical 5-header map. The `body` parameter MUST be the
 * exact byte sequence sent on the wire (no whitespace re-canonicalization
 * between client serialization and signature basis construction).
 */
export function buildSignedHeaders(params: BuildSignedHeadersParams): DeviceSignedHeaders {
	const timestamp = params.timestamp ?? Date.now();
	const signature = createDeviceRequestSignature({
		method: params.method,
		path: params.path,
		timestamp,
		body: params.body,
		secret: params.deviceSecret,
	});
	return {
		[DeviceAuthHeaders.deviceId]: params.publicId,
		[DeviceAuthHeaders.deviceSecret]: params.deviceSecret,
		[DeviceAuthHeaders.signature]: signature,
		[DeviceAuthHeaders.timestamp]: String(timestamp),
		[DeviceAuthHeaders.secretVersion]: String(params.secretVersion),
	};
}
